import { createClient } from '@supabase/supabase-js';
import Constants from 'expo-constants';
import { LogBox } from 'react-native';
import { timeoutLogger, TimeoutErrorCode, TimeoutSeverity } from '@/utils/timeoutLogger';
import { createTracer } from '@/utils/requestTracer';

// Suppress Supabase Realtime warnings since we're not using realtime features
LogBox.ignoreLogs([
  'Sending `on_ready` with no listeners registered',
  'Sending `on_sync_done` with no listeners registered',
  'Sending `on_sync_ready` with no listeners registered',
]);

// Override console.warn to filter out Supabase Realtime warnings
const originalWarn = console.warn;
console.warn = (...args: any[]) => {
  const message = args[0];
  if (typeof message === 'string') {
    // Filter out Supabase Realtime event warnings
    if (
      message.includes('Sending `on_ready` with no listeners registered') ||
      message.includes('Sending `on_sync_done` with no listeners registered') ||
      message.includes('Sending `on_sync_ready` with no listeners registered')
    ) {
      return; // Suppress these specific warnings
    }
  }
  // Pass through all other warnings
  originalWarn.apply(console, args);
};

// Prioritize Constants.expoConfig.extra, then process.env, then fallback values
export const supabaseUrl = 
  Constants.expoConfig?.extra?.SUPABASE_URL ||
  process.env.EXPO_PUBLIC_SUPABASE_URL || 
  'https://lyxcrziihvhucgtftakw.supabase.co';

const supabaseAnonKey =
  Constants.expoConfig?.extra?.SUPABASE_ANON_KEY ||
  process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY ||
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imx5eGNyemlpaHZodWNndGZ0YWt3Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzkzNTg4NTUsImV4cCI6MjA5NDkzNDg1NX0.FrU9J7BIc9RpZIkJLwavVwlQ1V6bwYPJOHBXfTBTq-8';

// Log Supabase configuration for debugging
/*console.log('🔧 Supabase Configuration:', {
  url: supabaseUrl,
  hasAnonKey: !!supabaseAnonKey,
  anonKeyLength: supabaseAnonKey?.length || 0,
  anonKeyPrefix: supabaseAnonKey?.substring(0, 20) + '...',
  sourceUrl: Constants.expoConfig?.extra?.SUPABASE_URL ? 'expo-config' : 
             process.env.EXPO_PUBLIC_SUPABASE_URL ? 'process-env' : 'fallback',
  sourceKey: Constants.expoConfig?.extra?.SUPABASE_ANON_KEY ? 'expo-config' : 
             process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY ? 'process-env' : 'fallback',
  timestamp: new Date().toISOString()
});*/

// Validate Supabase URL format
try {
  new URL(supabaseUrl);
} catch (error) {
  console.error('❌ Invalid Supabase URL:', supabaseUrl);
}

export const supabase = (() => {
  try {
    const client = createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        persistSession: true,
      },
      realtime: {
        params: {
          eventsPerSecond: 0,
        },
        logger: () => {},
      },
      global: {
        fetch: async (url, options) => {
          const timeout = 10000;
          const tracer = createTracer(`Supabase Fetch: ${url}`, timeout);
          const controller = new AbortController();
          const startTime = Date.now();

          const timeoutId = setTimeout(() => {
            const actualDuration = Date.now() - startTime;
            tracer.timeout(actualDuration);

            timeoutLogger.logTimeout({
              requestId: tracer.getRequestId(),
              operationName: `Supabase Fetch: ${url}`,
              timeoutDuration: timeout,
              actualDuration,
              errorCode: TimeoutErrorCode.SUPABASE_QUERY,
              severity: TimeoutSeverity.HIGH,
              metadata: {
                url,
                method: options?.method || 'GET',
              },
            }).catch(err => console.error('Failed to log timeout:', err));

            controller.abort();
          }, timeout);

          try {
            tracer.addCheckpoint('Fetch started', { url, method: options?.method || 'GET' });

            const response = await fetch(url, {
              ...options,
              signal: controller.signal,
            });

            clearTimeout(timeoutId);
            tracer.addCheckpoint('Fetch completed', { status: response.status });
            tracer.complete({ status: response.status, url });

            return response;
          } catch (error: any) {
            clearTimeout(timeoutId);

            if (error?.name === 'AbortError') {
              const actualDuration = Date.now() - startTime;
              console.error(`❌ [SUPABASE-TIMEOUT] Request aborted after ${actualDuration}ms: ${url}`);
            }

            throw error;
          }
        },
      },
    });

    return client;
  } catch (error) {
    console.error('❌ Failed to create Supabase client:', error);
    throw error;
  }
})();

export async function validateSupabaseConnection(): Promise<boolean> {
  try {
    console.log('🔍 Validating Supabase connection...');

    const { data, error } = await supabase
      .from('users')
      .select('id')
      .limit(1)
      .maybeSingle();

    if (error && error.code !== 'PGRST116') {
      console.error('❌ Supabase connection validation failed:', error);
      return false;
    }

    console.log('✅ Supabase connection validated successfully');
    return true;
  } catch (error) {
    console.error('❌ Supabase connection validation error:', error);
    return false;
  }
}

export function getSupabaseConnectionInfo() {
  return {
    url: supabaseUrl,
    hasAnonKey: !!supabaseAnonKey,
    sourceUrl: Constants.expoConfig?.extra?.SUPABASE_URL ? 'expo-config' :
               process.env.EXPO_PUBLIC_SUPABASE_URL ? 'process-env' : 'fallback',
    sourceKey: Constants.expoConfig?.extra?.SUPABASE_ANON_KEY ? 'expo-config' :
               process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY ? 'process-env' : 'fallback',
  };
}

